import { useMemo, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useBrands } from "../../features/brands/queries";
import { cn } from "@/lib/utils";

const lower = (s: string) => s.toLocaleLowerCase("tr");

/**
 * Marka çoklu seçimi. Seçim marka slug'larıyla tutulur (virgülle ayrılmış —
 * DB formatı); slug marka adından farklı olarak sabit kaldığı için marka adı
 * değişse de bağlantı bozulmaz.
 *
 * Blog yazısını markalarla ilişkilendirmek için kullanılır: marka detay
 * sayfasındaki "İlgili Yazılar" bölümü bu seçime göre doldurulur.
 */
export function BrandSelect({
  value,
  onChange,
}: {
  /** Seçili marka slug'ları (virgülle ayrılmış) */
  value: string;
  onChange: (v: string) => void;
}) {
  const { data: brands = [], isLoading } = useBrands();
  const [search, setSearch] = useState("");

  const selected = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const selectedSet = new Set(selected.map(lower));

  const adBySlug = useMemo(
    () => new Map(brands.map((b) => [lower(b.slug ?? ""), b.name])),
    [brands],
  );

  const filtered = useMemo(() => {
    const q = lower(search.trim());
    if (!q) return brands;
    return brands.filter((b) => lower(b.name).includes(q) || lower(b.slug ?? "").includes(q));
  }, [brands, search]);

  function toggle(slug: string) {
    if (selectedSet.has(lower(slug))) {
      onChange(selected.filter((s) => lower(s) !== lower(slug)).join(","));
    } else {
      onChange([...selected, slug].join(","));
    }
  }

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((slug) => (
            <Badge key={slug} variant="secondary" className="gap-1 pr-1">
              {adBySlug.get(lower(slug)) ?? slug}
              <button
                type="button"
                onClick={() => toggle(slug)}
                className="rounded-sm hover:bg-background/60 p-0.5"
                aria-label={`${adBySlug.get(lower(slug)) ?? slug} seçimini kaldır`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground"
            onClick={() => onChange("")}
          >
            Tümünü kaldır
          </Button>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Marka ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      <ScrollArea className="h-44 rounded-md border border-input">
        {isLoading ? (
          <p className="p-3 text-sm text-muted-foreground">Yükleniyor...</p>
        ) : filtered.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">Marka bulunamadı</p>
        ) : (
          <div className="p-1">
            {filtered.map((brand) => {
              const slug = brand.slug ?? "";
              const isSelected = selectedSet.has(lower(slug));
              return (
                <button
                  key={brand.id ?? slug}
                  type="button"
                  onClick={() => toggle(slug)}
                  className={cn(
                    "w-full flex items-center gap-2 rounded px-2 py-1.5 text-sm text-left",
                    "hover:bg-accent hover:text-accent-foreground",
                    isSelected && "bg-accent/60",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-input",
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{brand.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
